import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  BusinessEntity,
  EntityClassification,
  EntityInput,
  EntityPnL,
  EntityRecord,
  EntityRecordInput,
  EntityRecordKind,
  FinanceDocument,
  FinancialAccount,
  Form1099Input,
  Form1099Record,
  PnLLine,
} from '../../types/family-finance'
import { CLASSIFICATION_LABELS, RECORD_KIND_LABELS } from '../../types/family-finance'
import {
  add1099,
  addEntityRecord,
  delete1099,
  deleteEntity,
  deleteEntityRecord,
  deletePnL,
  list1099s,
  listDocuments,
  listEntityRecords,
  listPnL,
  savePnL,
  update1099,
  updateEntity,
  updateEntityRecord,
} from '../../lib/family-finance-api'
import DocumentUploader from './DocumentUploader'
import { DocumentList } from './DocumentsTab'
import {
  Card,
  Chip,
  EmptyState,
  LoadError,
  LoadingRows,
  errorMessage,
  inputClass,
  money,
  primaryBtn,
  secondaryBtn,
  shortDate,
  signedMoney,
} from './shared'

const SECTIONS = [
  { id: 'details', label: 'Details' },
  { id: 'pnl', label: 'P&L' },
  { id: 'forms', label: '1099s' },
  { id: 'records', label: 'Records' },
  { id: 'documents', label: 'Documents' },
] as const

type SectionId = (typeof SECTIONS)[number]['id']

const F1099_TYPES: Form1099Record['type'][] = ['1099-NEC', '1099-MISC', '1099-K', '1099-INT', '1099-DIV']
const F1099_STATUSES: Form1099Record['status'][] = ['draft', 'needs-w9', 'filed', 'received']
const YEARS = [2026, 2025, 2024, 2023, 2022]

function Field({
  label,
  children,
  className = '',
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <label className={`block ${className}`}>
      <span className="text-slate-500 dark:text-slate-400 text-xs">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}

// ─── Details ─────────────────────────────────────────────────

function DetailsSection({
  entity,
  onChanged,
  onDeleted,
}: {
  entity: BusinessEntity
  onChanged: () => void
  onDeleted: () => void
}) {
  const [form, setForm] = useState<EntityInput>({
    name: entity.name,
    shortName: entity.shortName,
    state: entity.state,
    classification: entity.classification,
    members: entity.members,
    description: entity.description,
    icon: entity.icon,
    ein: entity.ein ?? '',
    formedOn: entity.formedOn ?? null,
    registeredAgent: entity.registeredAgent ?? '',
  })
  const [membersText, setMembersText] = useState(entity.members.join(', '))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  function set<K extends keyof EntityInput>(key: K, value: EntityInput[K]) {
    setForm((f) => ({ ...f, [key]: value }))
    setSaved(false)
  }

  async function save() {
    if (!form.name.trim()) {
      setError('The legal name is required.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await updateEntity(entity.id, {
        ...form,
        name: form.name.trim(),
        shortName: form.shortName.trim() || form.name.trim(),
        members: membersText
          .split(',')
          .map((m) => m.trim())
          .filter(Boolean),
      })
      setSaved(true)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (
      !window.confirm(
        `Delete ${entity.name}? Its P&Ls, 1099 records, and records are removed too. Documents are kept but unlinked from the business.`,
      )
    )
      return
    setBusy(true)
    try {
      await deleteEntity(entity.id)
      onDeleted()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Business details" subtitle="Everything here is owner-entered — nothing is inferred">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Legal name" className="sm:col-span-2">
          <input value={form.name} onChange={(e) => set('name', e.target.value)} className={inputClass} />
        </Field>
        <Field label="Short name">
          <input value={form.shortName} onChange={(e) => set('shortName', e.target.value)} className={inputClass} />
        </Field>
        <Field label="Icon">
          <input
            value={form.icon}
            onChange={(e) => set('icon', e.target.value)}
            maxLength={4}
            placeholder="🏛️"
            className={inputClass}
          />
        </Field>
        <Field label="Tax classification" className="sm:col-span-2">
          <select
            value={form.classification}
            onChange={(e) => set('classification', e.target.value as EntityClassification)}
            className={inputClass}
          >
            {(Object.keys(CLASSIFICATION_LABELS) as EntityClassification[]).map((c) => (
              <option key={c} value={c}>{CLASSIFICATION_LABELS[c]}</option>
            ))}
          </select>
        </Field>
        <Field label="State">
          <input value={form.state} onChange={(e) => set('state', e.target.value)} maxLength={2} className={inputClass} />
        </Field>
        <Field label="EIN">
          <input
            value={form.ein}
            onChange={(e) => set('ein', e.target.value)}
            placeholder="12-3456789"
            className={inputClass}
          />
        </Field>
        <Field label="Formed on">
          <input
            type="date"
            value={form.formedOn ?? ''}
            onChange={(e) => set('formedOn', e.target.value || null)}
            className={inputClass}
          />
        </Field>
        <Field label="Registered agent" className="sm:col-span-2">
          <input
            value={form.registeredAgent}
            onChange={(e) => set('registeredAgent', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Members / owners (comma-separated)" className="sm:col-span-2 lg:col-span-4">
          <input
            value={membersText}
            onChange={(e) => {
              setMembersText(e.target.value)
              setSaved(false)
            }}
            className={inputClass}
          />
        </Field>
        <Field label="Description" className="sm:col-span-2 lg:col-span-4">
          <textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
            className={inputClass}
          />
        </Field>
      </div>

      {error && <p className="text-red-600 dark:text-red-400 text-sm mt-3">{error}</p>}

      <div className="flex items-center justify-between gap-3 mt-4">
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={busy} className={primaryBtn}>
            {busy ? 'Saving…' : 'Save details'}
          </button>
          {saved && <span className="text-emerald-600 dark:text-emerald-400 text-sm">Saved ✓</span>}
        </div>
        <button
          onClick={remove}
          disabled={busy}
          className="px-3 py-1.5 text-red-600/90 dark:text-red-400/90 hover:text-red-700 dark:hover:text-red-300 text-sm"
        >
          Delete business
        </button>
      </div>
    </Card>
  )
}

// ─── P&L ─────────────────────────────────────────────────────

interface PnLDraft {
  year: number
  through: string
  revenue: number
  expenses: PnLLine[]
}

function PnLSection({
  entity,
  pnls,
  onChanged,
}: {
  entity: BusinessEntity
  pnls: EntityPnL[]
  onChanged: () => void
}) {
  const mine = useMemo(
    () => pnls.filter((p) => p.entityId === entity.id).sort((a, b) => b.year - a.year),
    [entity.id, pnls],
  )
  const [year, setYear] = useState<number>(mine[0]?.year ?? new Date().getFullYear())
  const [draft, setDraft] = useState<PnLDraft | null>(null)
  const [busy, setBusy] = useState(false)

  const current = mine.find((p) => p.year === year) ?? null
  const editing = draft !== null
  const view = draft ?? current

  const netIncome = view
    ? view.revenue - view.expenses.reduce((sum, e) => sum + e.amount, 0)
    : 0

  function startEdit() {
    setDraft(
      current
        ? { year: current.year, through: current.through, revenue: current.revenue, expenses: [...current.expenses] }
        : { year, through: `${year}-12-31`, revenue: 0, expenses: [{ category: '', amount: 0 }] },
    )
  }

  function startNewYear() {
    const taken = new Set(mine.map((p) => p.year))
    const nextYear = YEARS.find((y) => !taken.has(y)) ?? year
    setYear(nextYear)
    setDraft({ year: nextYear, through: `${nextYear}-12-31`, revenue: 0, expenses: [{ category: '', amount: 0 }] })
  }

  async function save() {
    if (!draft) return
    setBusy(true)
    try {
      await savePnL({ entityId: entity.id, ...draft })
      setYear(draft.year)
      setDraft(null)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!current) return
    if (!window.confirm(`Delete the ${current.year} P&L for ${entity.shortName}?`)) return
    setBusy(true)
    try {
      await deletePnL(entity.id, current.year)
      setDraft(null)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Profit & loss"
      subtitle="Net income is always computed from revenue minus expense lines"
      action={
        <div className="flex items-center gap-2">
          {!editing && mine.length > 0 && (
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm rounded-lg px-2 py-1.5 border border-slate-300 dark:border-slate-600 outline-none"
            >
              {mine.map((p) => (
                <option key={p.year} value={p.year}>{p.year}</option>
              ))}
            </select>
          )}
          {!editing && (
            <>
              <button onClick={startEdit} className={secondaryBtn}>
                {current ? 'Edit' : 'Add'}
              </button>
              <button onClick={startNewYear} className={primaryBtn}>+ Year</button>
            </>
          )}
        </div>
      }
    >
      {!view ? (
        <EmptyState icon="📓" message={`No P&L recorded for ${entity.shortName} yet — add one to feed the tax estimator.`} />
      ) : editing && draft ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Year">
              <select
                value={draft.year}
                onChange={(e) => {
                  const nextYear = parseInt(e.target.value, 10)
                  setDraft({ ...draft, year: nextYear, through: `${nextYear}-12-31` })
                }}
                className={inputClass}
              >
                {YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Field>
            <Field label="Data through">
              <input
                type="date"
                value={draft.through}
                onChange={(e) => setDraft({ ...draft, through: e.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Revenue">
              <input
                type="number"
                value={draft.revenue}
                onChange={(e) => setDraft({ ...draft, revenue: parseFloat(e.target.value) || 0 })}
                className={inputClass}
              />
            </Field>
          </div>

          <div>
            <p className="text-slate-500 dark:text-slate-400 text-xs mb-1.5">Expense lines</p>
            <div className="space-y-2">
              {draft.expenses.map((line, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={line.category}
                    onChange={(e) => {
                      const expenses = [...draft.expenses]
                      expenses[i] = { ...line, category: e.target.value }
                      setDraft({ ...draft, expenses })
                    }}
                    placeholder="Category (e.g. Contractors & payroll)"
                    className={`${inputClass} flex-1`}
                  />
                  <input
                    type="number"
                    value={line.amount}
                    onChange={(e) => {
                      const expenses = [...draft.expenses]
                      expenses[i] = { ...line, amount: parseFloat(e.target.value) || 0 }
                      setDraft({ ...draft, expenses })
                    }}
                    className={`${inputClass} w-32`}
                  />
                  <button
                    onClick={() =>
                      setDraft({ ...draft, expenses: draft.expenses.filter((_, j) => j !== i) })
                    }
                    className="text-slate-500 hover:text-red-600 dark:hover:text-red-400 text-sm px-1"
                    title="Remove line"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setDraft({ ...draft, expenses: [...draft.expenses, { category: '', amount: 0 }] })}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm mt-2"
            >
              + Add expense line
            </button>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
            <span className="text-slate-800 dark:text-slate-200 text-sm font-medium">Net income</span>
            <span className={`font-bold ${netIncome >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {signedMoney(netIncome)}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={save} disabled={busy} className={primaryBtn}>
              {busy ? 'Saving…' : 'Save P&L'}
            </button>
            <button onClick={() => setDraft(null)} disabled={busy} className={secondaryBtn}>Cancel</button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between text-sm py-1.5 border-b border-slate-200/50 dark:border-slate-700/50">
            <span className="text-slate-600 dark:text-slate-300">Revenue</span>
            <span className="text-slate-900 dark:text-white font-medium">{money(view.revenue)}</span>
          </div>
          {view.expenses.map((line) => (
            <div key={line.category} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-200/50 dark:border-slate-700/50">
              <span className="text-slate-500 pl-3">{line.category}</span>
              <span className="text-slate-500 dark:text-slate-400">−{money(line.amount)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between text-sm py-2">
            <span className="text-slate-800 dark:text-slate-200 font-medium">Net income</span>
            <span className={`font-bold ${netIncome >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {signedMoney(netIncome)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-slate-400 dark:text-slate-600 text-xs">Through {shortDate(view.through)}</p>
            <button
              onClick={remove}
              disabled={busy}
              className="text-red-600/80 dark:text-red-400/80 hover:text-red-700 dark:hover:text-red-300 text-xs"
            >
              Delete {view.year} P&L
            </button>
          </div>
        </div>
      )}
    </Card>
  )
}

// ─── 1099s ───────────────────────────────────────────────────

const BLANK_1099 = (entityId: string): Form1099Input => ({
  entityId,
  direction: 'issued',
  type: '1099-NEC',
  counterparty: '',
  amount: 0,
  taxYear: new Date().getFullYear(),
  status: 'draft',
})

function Forms1099Section({
  entity,
  forms,
  onChanged,
}: {
  entity: BusinessEntity
  forms: Form1099Record[]
  onChanged: () => void
}) {
  const mine = forms.filter((f) => f.entityId === entity.id)
  const [draft, setDraft] = useState<Form1099Input | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    if (!draft) return
    if (!draft.counterparty.trim()) {
      setError('A counterparty name is required.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const payload = { ...draft, counterparty: draft.counterparty.trim() }
      if (editingId) await update1099(editingId, payload)
      else await add1099(payload)
      setDraft(null)
      setEditingId(null)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  async function remove(record: Form1099Record) {
    if (!window.confirm(`Delete the ${record.type} record for ${record.counterparty}?`)) return
    setBusy(true)
    try {
      await delete1099(record.id)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="1099 records"
      subtitle={`Issued and received by ${entity.shortName}`}
      action={
        <button
          onClick={() => {
            setEditingId(null)
            setDraft(draft && !editingId ? null : BLANK_1099(entity.id))
          }}
          className={primaryBtn}
        >
          {draft && !editingId ? 'Close' : '+ Add 1099'}
        </button>
      }
    >
      {draft && (
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4 p-3 bg-slate-100/60 dark:bg-slate-900/60 rounded-xl">
          <Field label="Direction">
            <select
              value={draft.direction}
              onChange={(e) => setDraft({ ...draft, direction: e.target.value as Form1099Record['direction'] })}
              className={inputClass}
            >
              <option value="issued">Issued</option>
              <option value="received">Received</option>
            </select>
          </Field>
          <Field label="Form">
            <select
              value={draft.type}
              onChange={(e) => setDraft({ ...draft, type: e.target.value as Form1099Record['type'] })}
              className={inputClass}
            >
              {F1099_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
          <Field label="Counterparty" className="sm:col-span-2">
            <input
              value={draft.counterparty}
              onChange={(e) => setDraft({ ...draft, counterparty: e.target.value })}
              placeholder="Contractor, platform, or payer"
              className={inputClass}
            />
          </Field>
          <Field label="Amount">
            <input
              type="number"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: parseFloat(e.target.value) || 0 })}
              className={inputClass}
            />
          </Field>
          <Field label="Tax year">
            <select
              value={draft.taxYear}
              onChange={(e) => setDraft({ ...draft, taxYear: parseInt(e.target.value, 10) })}
              className={inputClass}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </Field>
          <Field label="Status" className="sm:col-span-2">
            <select
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value as Form1099Record['status'] })}
              className={inputClass}
            >
              {F1099_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
          {error && <p className="text-red-600 dark:text-red-400 text-sm sm:col-span-3 lg:col-span-6">{error}</p>}
          <div className="sm:col-span-3 lg:col-span-6 flex items-center gap-2">
            <button onClick={save} disabled={busy} className={primaryBtn}>
              {busy ? 'Saving…' : editingId ? 'Save changes' : 'Add record'}
            </button>
            <button
              onClick={() => {
                setDraft(null)
                setEditingId(null)
              }}
              disabled={busy}
              className={secondaryBtn}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {mine.length === 0 ? (
        <EmptyState icon="📋" message="No 1099s recorded for this business yet." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200 dark:border-slate-700">
                <th className="text-left py-2 pr-3 font-medium">Form</th>
                <th className="text-left py-2 pr-3 font-medium">Counterparty</th>
                <th className="text-right py-2 pr-3 font-medium">Amount</th>
                <th className="text-left py-2 pr-3 font-medium hidden sm:table-cell">TY</th>
                <th className="text-left py-2 pr-3 font-medium">Status</th>
                <th className="text-right py-2 font-medium">Edit</th>
              </tr>
            </thead>
            <tbody>
              {mine.map((f) => (
                <tr key={f.id} className="border-b border-slate-200/50 dark:border-slate-700/50 last:border-0">
                  <td className="py-2.5 pr-3">
                    <p className="text-slate-900 dark:text-white">{f.type}</p>
                    <p className="text-slate-500 text-xs">{f.direction}</p>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-600 dark:text-slate-300">{f.counterparty}</td>
                  <td className="py-2.5 pr-3 text-right text-slate-900 dark:text-white font-medium">{money(f.amount)}</td>
                  <td className="py-2.5 pr-3 text-slate-500 dark:text-slate-400 hidden sm:table-cell">{f.taxYear}</td>
                  <td className="py-2.5 pr-3">
                    <Chip tone={f.status === 'needs-w9' ? 'red' : f.status === 'draft' ? 'amber' : f.status === 'filed' ? 'green' : 'slate'}>
                      {f.status}
                    </Chip>
                  </td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    <button
                      onClick={() => {
                        const { id: _id, ...rest } = f
                        setEditingId(f.id)
                        setDraft(rest)
                        setError(null)
                      }}
                      className="text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 px-1"
                      title="Edit"
                    >
                      ✎
                    </button>
                    <button
                      onClick={() => remove(f)}
                      disabled={busy}
                      className="text-slate-500 hover:text-red-600 dark:hover:text-red-400 px-1"
                      title="Delete"
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

// ─── Records ─────────────────────────────────────────────────

function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const BLANK_RECORD = (entityId: string): EntityRecordInput => ({
  entityId,
  kind: 'note',
  title: '',
  date: todayIso(),
  amount: null,
  notes: '',
})

function RecordsSection({
  entity,
  records,
  onChanged,
}: {
  entity: BusinessEntity
  records: EntityRecord[]
  onChanged: () => void
}) {
  const mine = records.filter((r) => r.entityId === entity.id)
  const [draft, setDraft] = useState<EntityRecordInput | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    if (!draft) return
    if (!draft.title.trim()) {
      setError('Give the record a title.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (editingId) await updateEntityRecord(editingId, draft)
      else await addEntityRecord(draft)
      setDraft(null)
      setEditingId(null)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  async function remove(record: EntityRecord) {
    if (!window.confirm(`Delete “${record.title}”?`)) return
    setBusy(true)
    try {
      await deleteEntityRecord(record.id)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Records"
      subtitle="Capital contributions, distributions, resolutions, filings, assets, and notes"
      action={
        <button
          onClick={() => {
            setEditingId(null)
            setDraft(draft && !editingId ? null : BLANK_RECORD(entity.id))
          }}
          className={primaryBtn}
        >
          {draft && !editingId ? 'Close' : '+ Add record'}
        </button>
      }
    >
      {draft && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-4 p-3 bg-slate-100/60 dark:bg-slate-900/60 rounded-xl">
          <Field label="Kind">
            <select
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value as EntityRecordKind })}
              className={inputClass}
            >
              {(Object.keys(RECORD_KIND_LABELS) as EntityRecordKind[]).map((k) => (
                <option key={k} value={k}>{RECORD_KIND_LABELS[k]}</option>
              ))}
            </select>
          </Field>
          <Field label="Title" className="sm:col-span-2">
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="What happened"
              className={inputClass}
            />
          </Field>
          <Field label="Date">
            <input
              type="date"
              value={draft.date}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
              className={inputClass}
            />
          </Field>
          <Field label="Amount (optional)">
            <input
              type="number"
              value={draft.amount ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, amount: e.target.value === '' ? null : parseFloat(e.target.value) || 0 })
              }
              className={inputClass}
            />
          </Field>
          <Field label="Notes" className="sm:col-span-3">
            <input
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              className={inputClass}
            />
          </Field>
          {error && <p className="text-red-600 dark:text-red-400 text-sm sm:col-span-4">{error}</p>}
          <div className="sm:col-span-4 flex items-center gap-2">
            <button onClick={save} disabled={busy} className={primaryBtn}>
              {busy ? 'Saving…' : editingId ? 'Save changes' : 'Add record'}
            </button>
            <button
              onClick={() => {
                setDraft(null)
                setEditingId(null)
              }}
              disabled={busy}
              className={secondaryBtn}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {mine.length === 0 ? (
        <EmptyState icon="🗒️" message="No records yet — log a contribution, distribution, resolution, or note." />
      ) : (
        <div className="space-y-1">
          {mine.map((r) => (
            <div
              key={r.id}
              className="flex items-start justify-between gap-3 py-2 border-b border-slate-200/50 dark:border-slate-700/50 last:border-0"
            >
              <div className="min-w-0">
                <p className="text-slate-900 dark:text-white text-sm">{r.title}</p>
                <p className="text-slate-500 text-xs">
                  {RECORD_KIND_LABELS[r.kind]} · {shortDate(r.date)}
                  {r.amount !== null && ` · ${money(r.amount)}`}
                </p>
                {r.notes && <p className="text-slate-400 dark:text-slate-500 text-xs mt-0.5">{r.notes}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => {
                    setEditingId(r.id)
                    setDraft({
                      entityId: r.entityId,
                      kind: r.kind,
                      title: r.title,
                      date: r.date,
                      amount: r.amount,
                      notes: r.notes,
                    })
                    setError(null)
                  }}
                  className="text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 text-sm px-1"
                  title="Edit"
                >
                  ✎
                </button>
                <button
                  onClick={() => remove(r)}
                  disabled={busy}
                  className="text-slate-500 hover:text-red-600 dark:hover:text-red-400 text-sm px-1"
                  title="Delete"
                >
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

// ─── Workspace ───────────────────────────────────────────────

export default function EntityWorkspace({
  entity,
  entities,
  accounts,
  onChanged,
  onBack,
}: {
  entity: BusinessEntity
  entities: BusinessEntity[]
  accounts: FinancialAccount[]
  onChanged: () => void
  onBack: () => void
}) {
  const [section, setSection] = useState<SectionId>('details')
  const [pnls, setPnls] = useState<EntityPnL[]>([])
  const [forms, setForms] = useState<Form1099Record[]>([])
  const [records, setRecords] = useState<EntityRecord[]>([])
  const [docs, setDocs] = useState<FinanceDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      const [p, f, r, d] = await Promise.all([
        listPnL(),
        list1099s(),
        listEntityRecords(),
        listDocuments(),
      ])
      setPnls(p)
      setForms(f)
      setRecords(r)
      setDocs(d)
      setLoadError(null)
    } catch (err) {
      setLoadError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  function refreshAll() {
    reload()
    onChanged()
  }

  const myDocs = docs.filter((d) => d.entityId === entity.id)
  const counts = {
    pnl: pnls.filter((p) => p.entityId === entity.id).length,
    forms: forms.filter((f) => f.entityId === entity.id).length,
    records: records.filter((r) => r.entityId === entity.id).length,
    documents: myDocs.length,
  }

  return (
    <div className="space-y-4">
      {/* Entity header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm"
          >
            ← All businesses
          </button>
          <span className="text-2xl">{entity.icon}</span>
          <div>
            <p className="text-slate-900 dark:text-white font-semibold">{entity.name}</p>
            <p className="text-slate-500 text-xs">
              {entity.state} · {CLASSIFICATION_LABELS[entity.classification]}
              {entity.ein && ` · EIN ${entity.ein}`}
            </p>
          </div>
        </div>
        {entity.classificationAssumed && <Chip tone="amber">classification assumed — confirm below</Chip>}
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {SECTIONS.map((s) => {
          const count = s.id === 'details' ? null : counts[s.id]
          return (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                section === s.id
                  ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
              }`}
            >
              {s.label}
              {count !== null && count > 0 && <span className="ml-1.5 opacity-60">{count}</span>}
            </button>
          )
        })}
      </div>

      {loadError ? (
        <LoadError error={loadError} onRetry={reload} />
      ) : loading ? (
        <LoadingRows rows={4} />
      ) : (
        <>
          {section === 'details' && (
            <DetailsSection entity={entity} onChanged={onChanged} onDeleted={onBack} />
          )}
          {section === 'pnl' && <PnLSection entity={entity} pnls={pnls} onChanged={refreshAll} />}
          {section === 'forms' && <Forms1099Section entity={entity} forms={forms} onChanged={refreshAll} />}
          {section === 'records' && (
            <RecordsSection entity={entity} records={records} onChanged={refreshAll} />
          )}
          {section === 'documents' && (
            <div className="space-y-6">
              <Card
                title={`Upload to ${entity.shortName}`}
                subtitle="Formation docs, operating agreements, contracts, invoices, ledgers, insurance — filed under Businesses / this entity"
              >
                <DocumentUploader
                  entities={entities}
                  accounts={accounts}
                  onUploaded={refreshAll}
                  lockTo={{ folder: 'businesses', entityId: entity.id }}
                  hint={`Files dropped here are filed under Businesses / ${entity.shortName}.`}
                />
              </Card>
              <Card title={`${entity.shortName} documents`} subtitle={`${myDocs.length} on file`}>
                <DocumentList
                  docs={myDocs}
                  entities={entities}
                  accounts={accounts}
                  onChanged={refreshAll}
                  emptyMessage={`No documents filed for ${entity.shortName} yet — drop one in above.`}
                />
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  )
}
